import { HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { PagedResponse } from '../core';
import {
  ArchiveTaxRequest,
  CalculateLineItemTaxesRequest,
  CalculateTaxRequest,
  CreateTaxRequest,
  GetMultiTaxRequest,
  ListTaxRequest,
  PagedRequestOptions,
  TaxApiService,
  UpdateTaxRequest,
  CalculateLineItemTaxesResponse,
  CreateTaxResponse,
  GetTaxProviderRequest,
  GetTaxProviderResponse,
} from '@vendasta/billing';
import {
  CalculateLineItemsTaxResponse,
  CalculateTaxResponse,
  ListTaxRateFilter,
  TaxRate,
  taxRateFromApi,
  LineItem,
} from './tax-rate';

@Injectable({ providedIn: 'root' })
export class TaxRateService {
  constructor(private taxRateApi: TaxApiService) {}

  // NOTE: use calculateLineItems for more accurate precision
  calculate(merchantId: string, customerId: string, amount: number): Observable<CalculateTaxResponse> {
    const r = new CalculateTaxRequest({
      merchantId: merchantId,
      customerId: customerId,
      amount: amount,
    });
    return this.taxRateApi.calculate(r).pipe(
      map((response) => {
        const taxRates = (response.taxRates || []).map((item) => taxRateFromApi(item));
        return {
          taxableAmount: response.taxableAmount || 0,
          taxRates: taxRates,
        };
      }),
    );
  }

  // calculateLineItems calculates tax per line item and in total with a precision of 4 decimal places
  calculateLineItems(
    merchantId: string,
    customerId: string,
    lineItems: LineItem[],
  ): Observable<CalculateLineItemsTaxResponse> {
    const r = new CalculateLineItemTaxesRequest({
      merchantId: merchantId,
      customerId: customerId,
      lineItems: lineItems,
    });
    return this.taxRateApi.calculateLineItems(r).pipe(
      map((response) => {
        return this.buildCalculateLineItemsTaxResponse(response);
      }),
      catchError(() => {
        return of({} as CalculateLineItemsTaxResponse);
      }),
    );
  }

  private buildCalculateLineItemsTaxResponse(response: CalculateLineItemTaxesResponse): CalculateLineItemsTaxResponse {
    const taxRates = (response.taxRates || []).map((item) => taxRateFromApi(item));
    const lineItemTaxes = (response.lineItemTaxes || []).map((item) => {
      return {
        sku: item.sku,
        amount: item.taxableAmount || 0,
        taxRateIds: item.taxRateIds || [],
      };
    });
    return {
      appliedTaxes: response.appliedTaxes || [],
      lineItemTaxes: lineItemTaxes,
      totalTaxableAmount: response.totalTaxableAmount || 0,
      taxRates: taxRates,
    };
  }

  create(
    merchantId: string,
    countryCode: string,
    stateCode: string,
    name: string,
    description: string,
    percentage: number,
    accountingSystemId: string,
  ): Observable<CreateTaxResponse> {
    const r = new CreateTaxRequest({
      merchantId: merchantId,
      countryCode: countryCode,
      stateCode: stateCode,
      name: name,
      description: description,
      percentage: percentage,
      accountingSystemId: accountingSystemId,
    });

    return this.taxRateApi.create(r);
  }

  update(
    merchantId: string,
    id: string,
    countryCode: string,
    stateCode: string,
    name: string,
    description: string,
    percentage: number,
    accountingSystemId: string,
  ): Observable<HttpResponse<null>> {
    const r = new UpdateTaxRequest({
      merchantId: merchantId,
      id: id,
      countryCode: countryCode,
      stateCode: stateCode,
      name: name,
      description: description,
      percentage: percentage,
      accountingSystemId: accountingSystemId,
    });

    return this.taxRateApi.update(r);
  }

  list(filters: ListTaxRateFilter, cursor: string, pageSize: number): Observable<PagedResponse<TaxRate>> {
    const req = new ListTaxRequest({
      filters: filters,
      pagingOptions: new PagedRequestOptions({ cursor, pageSize }),
    });
    return this.taxRateApi.list(req).pipe(
      map((response) => {
        const taxRates = (response.taxRates || []).map((item) => taxRateFromApi(item));
        return new PagedResponse(taxRates, response.pagingMetadata.nextCursor, response.pagingMetadata.hasMore);
      }),
    );
  }

  getMulti(merchantId: string, ids: string[]): Observable<TaxRate[]> {
    const req = new GetMultiTaxRequest({
      merchantId: merchantId,
      ids: ids,
    });
    return this.taxRateApi.getMulti(req).pipe(
      map((response) => {
        const taxRates = (response.taxRates || []).map((item) => taxRateFromApi(item));
        return taxRates;
      }),
    );
  }

  archive(merchantId: string, id: string): Observable<HttpResponse<null>> {
    const req = new ArchiveTaxRequest({
      merchantId,
      id,
    });
    return this.taxRateApi.archive(req);
  }

  getTaxProvider(merchantId: string): Observable<GetTaxProviderResponse> {
    const req = new GetTaxProviderRequest({
      merchantId: merchantId,
    });
    return this.taxRateApi.getTaxProvider(req);
  }
}
